import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Banner, BannerLarge, Layout } from '@loadsmart/loadsmart-ui'

import { RequestStatus } from 'core/domain/Request'
import { formatRate } from 'core/presenters/laneRatePresenter'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { DownloadAppBanner } from 'core/ui/components/download-app-banner/DownloadAppBanner'
import { Icon } from 'core/ui/components/icon/Icon'
import { SignUpBanner } from 'core/ui/components/sign-up-banner/SignUpBanner'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { MainContent } from 'core/ui/MainContent'

import { user } from 'legacy/utils/user'

import { type DraftQuote } from '../domain/DraftQuote'
import { type AccessorialCharge } from '../domain/QuoteAccessorial'
import { hasRateValue } from '../domain/Rate'
import { getFlatRate } from '../domain/RateLine'
import { canReceiveProposals, checkAccessorialRateChanges } from '../domain/SpotQuote'
import { useAccessorialsHistory } from '../hooks/useAccessorialsHistory'
import { useSpotQuote } from '../hooks/useSpotQuote'
import { EmptySpotQuoteState } from './EmptySpotQuoteState'
import { SpotQuoteDetails } from './SpotQuoteDetails'
import { SpotQuoteHeader } from './SpotQuoteHeader'
import { SpotQuotePageHeader } from './SpotQuotePageHeader'

export type SingleSpotQuotePageProps = {
  match: {
    params: {
      id: string
    }
  }
}

export function SingleSpotQuotePage({ match }: SingleSpotQuotePageProps) {
  const { id } = match.params

  const {
    spotQuote,
    spotQuoteStatus,
    submitSpotQuoteStatus,
    fetchSpotQuote,
    submitQuote,
    rejectQuote,
  } = useSpotQuote()
  const [draftQuote, setDraftQuote] = useState<DraftQuote | undefined>()
  const underNegotiation = spotQuote?.underNegotiation
  const isSubmitted =
    (hasRateValue(spotQuote?.flatRate) && !underNegotiation) ||
    submitSpotQuoteStatus === RequestStatus.Done
  const cannotReceiveProposals = !canReceiveProposals(spotQuote)
  const isSubmitting = submitSpotQuoteStatus === RequestStatus.Pending
  const isFlatRateUpdating =
    formatRate(draftQuote?.flatRate ?? '', false) !==
    formatRate(getFlatRate(spotQuote?.ratelines) ?? '', false)
  const isAccessorialRateUpdating = checkAccessorialRateChanges(
    draftQuote?.accessorials,
    spotQuote?.ratelines
  )
  const disabled = isSubmitting || cannotReceiveProposals
  const isRateUpdating = isFlatRateUpdating || isAccessorialRateUpdating
  const shouldDisableSubmit =
    !hasRateValue(`${draftQuote?.flatRate}`) || !isRateUpdating || disabled

  const shouldDisplaySignUpBanner = isSubmitted && !user.isLoggedIn()
  const shouldDisplayCounterOfferBanner = spotQuote?.negotiationNote && underNegotiation
  const { accessorialsHistory, fetchAccessorialsHistory } = useAccessorialsHistory()
  const abortController = useAbortController()

  function handleNotesChange(notes: string) {
    setDraftQuote(prev => ({
      ...prev,
      notes,
    }))
  }

  function handleRateChange(flatRate: number) {
    setDraftQuote(prev => ({
      ...prev,
      flatRate,
    }))
  }

  const handleAccessorialsChange = useCallback((accessorials: Array<AccessorialCharge>) => {
    setDraftQuote(prev => ({
      ...prev,
      accessorials,
    }))
  }, [])

  async function handleSubmit() {
    if (isSubmitting || !draftQuote?.flatRate) return

    try {
      const { flatRate, notes, accessorials } = draftQuote

      await submitQuote({
        id,
        flatRate,
        notes,
        accessorials: accessorials,
      })

      toast.success('Bid submitted successfully')

      //refetch spot quote to show the updated rate
      await fetchSpotQuote(id, abortController)
    } catch (error) {
      let errorMessage = 'There was an error saving the quote'
      if (error instanceof Error) {
        errorMessage = error.message
      }
      toast.error(errorMessage)
    }
  }

  async function handleRejectRate(reason: string) {
    try {
      await rejectQuote(id, reason)
      toast.success('Bid rejected successfully')

      await fetchSpotQuote(id, abortController)
    } catch (error) {
      let errorMessage = 'Not able to reject bid'
      if (error instanceof Error) {
        errorMessage = error.message
      }
      toast.error(errorMessage)
    }
  }

  useEffect(() => {
    const isFetchCompleted = spotQuoteStatus === RequestStatus.Done

    if (isFetchCompleted && spotQuote?.carrier) {
      // The flat rate needs to be retrieved from the ratelines
      setDraftQuote({ flatRate: Number(getFlatRate(spotQuote.ratelines)) })
    }
  }, [id, spotQuoteStatus, spotQuote])

  useEffect(() => {
    fetchSpotQuote(id, abortController)

    return () => {
      abortController.abort()
    }
  }, [id, fetchSpotQuote, abortController])

  useEffect(() => {
    if (spotQuote?.carrier) {
      fetchAccessorialsHistory(spotQuote.carrier.id, abortController)

      return () => {
        abortController.abort()
      }
    }
  }, [spotQuote, fetchAccessorialsHistory, abortController])

  return (
    <MainContent>
      <Layout.Stack>
        {spotQuote && <DownloadAppBanner />}
        <AsyncContent
          status={spotQuoteStatus}
          loadingState={<LoadingState />}
          errorState={<EmptySpotQuoteState kind="single" />}
        >
          {spotQuote && (
            <Layout.Stack space="m">
              <SpotQuotePageHeader shipper={spotQuote.shipper} carrier={spotQuote.carrier} />
              {cannotReceiveProposals && (
                <Banner title="This Quote is no longer accepting offers." dismissible={false} />
              )}
              {shouldDisplaySignUpBanner && <SignUpBanner />}
              <Layout.Stack space="m">
                <SpotQuoteHeader spotQuote={spotQuote} />

                {shouldDisplayCounterOfferBanner && (
                  <BannerLarge
                    description={spotQuote?.negotiationNote}
                    scale="large"
                    title="You received a counter offer in the lane below"
                    variant="warning"
                    icon={<Icon name="arrows-reverse" size="22" />}
                  />
                )}

                <SpotQuoteDetails
                  id={spotQuote.uuid}
                  spotQuote={spotQuote}
                  notes={spotQuote.notes}
                  currentFlatRate={draftQuote?.flatRate}
                  isSubmitted={isSubmitted}
                  disabled={disabled}
                  submitDisabled={shouldDisableSubmit}
                  onSubmit={handleSubmit}
                  accessorialsHistory={accessorialsHistory}
                  onNotesChange={handleNotesChange}
                  onRateChange={handleRateChange}
                  onAccessorialsChange={handleAccessorialsChange}
                  draftQuote={draftQuote}
                  accessorialCharges={draftQuote?.accessorials}
                  handleRejectRate={handleRejectRate}
                />
              </Layout.Stack>
            </Layout.Stack>
          )}
        </AsyncContent>
      </Layout.Stack>
    </MainContent>
  )
}
